/**
 * =========================================================================================
 * Activity Timeline - Grouped list of activities by date
 * =========================================================================================
 */

import React, { useMemo } from 'react';
import moment from 'moment';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import ActivityCard from './activity-card';
import UIcon from 'ubs-framework/v2/UIcon';

const SkeletonCard = ({ isLast }) => (
    <div className="u_flex">
        <div className="u_flex u_direction-column u_align-center u_mr-16">
            <div className="u_h-40 u_p-10 u_radius-8 u_bg-thead u_loading u_flex-shrink" />
            {!isLast && <div className="u_flex-1 u_timeline-connector" />}
        </div>
        <div className="u_flex-1-0 u_pb-12">
            <div className="u_bg-white u_radius-8 u_b-light u_px-16 u_py-12">
                <div className="u_flex-align u_mb-6">
                    <div className="u_h-14 u_width-60 u_bg-thead u_radius-3 u_loading u_mr-8" />
                    <div className="u_h-14 u_width-120 u_bg-thead u_radius-3 u_loading" />
                </div>
                <div className="u_h-12 u_width-200 u_bg-thead u_radius-3 u_loading u_mb-6" />
                <div className="u_h-12 u_width-80 u_bg-thead u_radius-3 u_loading" />
            </div>
        </div>
    </div>
);

const ActivityTimeline = React.memo(({ activities, totalRecords, dataLoading, hasMore, onLoadMore, loadingMore }) => {

    const groupedActivities = useMemo(() => {
        if (!activities || activities.length === 0) return {};

        const groups = {};
        activities.forEach(activity => {
            const date = moment(activity?.timestamp || activity?.createdAt);
            let label;

            if (date.isSame(moment(), 'day')) {
                label = 'Today';
            } else if (date.isSame(moment().subtract(1, 'day'), 'day')) {
                label = 'Yesterday';
            } else if (date.isSame(moment(), 'week')) {
                label = date.format('dddd');
            } else if (date.isSame(moment(), 'year')) {
                label = date.format('DD MMM');
            } else {
                label = date.format('DD MMM YYYY');
            }

            if (!groups[label]) groups[label] = [];
            groups[label].push(activity);
        });

        return groups;
    }, [activities]);

    if (dataLoading && (!activities || activities.length === 0)) {
        return (
            <div className="u_px-20 u_py-16">
                {[1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} isLast={i === 5} />)}
            </div>
        );
    }

    if (!activities || activities.length === 0) {
        return (
            <div className="u_flex u_direction-column u_align-center u_content-center u_py-30 u_px-20">
                <div className='u_mb-16'>
                    <UIcon color={COLOR.u_icon_bg} thumb={56} icon={<Icon.Clock size={32} fill={COLOR.u_icon_bg} />} type="square" />
                </div>
                <p className="u_font-16 u_fw-600 u_color-title u_mb-8">No Activities Yet</p>
                <p className="u_small-font u_color-font u_text-center">
                    Activity timeline will appear here once subscription actions occur.
                </p>
            </div>
        );
    }

    const dateKeys = Object.keys(groupedActivities);
    const allActivities = Object.values(groupedActivities).flat();

    return (
        <div className="u_px-20 u_py-16">
            {/* Activity count */}
            <div className="u_flex-align u_mb-12">
                <Icon.Clock size={14} fill={COLOR.u_icon_bg} className="u_mr-6" />
                <p className="u_small-font u_color-font">
                    Showing <span className="u_fw-600 u_color-title">{activities.length}</span> of <span className="u_fw-600 u_color-title">{totalRecords}</span> activities
                </p>
            </div>

            {/* Grouped timeline */}
            {dateKeys?.map((dateLabel, index) => {
                const group = groupedActivities[dateLabel];
                return (
                    <div key={index}>
                        {/* Date header */}
                        <div className="u_flex-align u_mb-10">
                            <div className="u_h-1 u_flex-1-0 u_bg-blue-2" />
                            <div className="u_flex-align u_bg-lightblue u_radius-5 u_px-10 u_py-4 u_mx-8">
                                <span className="u_small-font u_fw-600 u_color-blue u_ws-nowrap">
                                    {dateLabel}
                                </span>
                            </div>
                            <div className="u_h-1 u_flex-1-0 u_bg-blue-2" />
                        </div>

                        {/* Activities for this date */}
                        {group?.map((activity, idx) => {
                            const globalIdx = allActivities.indexOf(activity);
                            const isLastGlobal = globalIdx === allActivities.length - 1;

                            return (
                                <ActivityCard
                                    key={idx}
                                    activity={activity}
                                    isLast={isLastGlobal}
                                />
                            );
                        })}
                    </div>
                );
            })}

            {/* Load More */}
            {hasMore && (
                <div className="u_flex u_content-center u_mt-10 u_mb-16">
                    <div
                        className={`u_flex-align u_content-center u_px-16 u_py-8 u_radius-5 u_b-light u_bg-lightblue u_cp u_small-font u_fw-500 u_color-blue ${loadingMore ? 'u_disabled' : ''}`}
                        onClick={() => !loadingMore && onLoadMore()}
                    >
                        {loadingMore ? (
                            <React.Fragment>
                                <Icon.Preloader /> <span className="u_ml-8">Loading...</span>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Icon.DownArrow size={8} fill={COLOR.u_blue} />
                                <span className="u_ml-6">Load More</span>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

export default ActivityTimeline;
